import * as React from 'react';
import { Clock } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { cn } from '@/lib/utils';
import { getHoursFormat } from '@/config/hoursFormat';
import {
  formatHourOnlyForDisplay,
  getTimeSelectTriggerClass,
  hm24ToTwelveHourParts,
  normalizeWallClockHm,
  twelveHourPartsToHm24,
  TWELVE_HOUR_OPTIONS,
  WALL_CLOCK_HOUR_OPTIONS_24,
  WALL_CLOCK_MINUTE_OPTIONS,
} from '@/utils/timeDisplayFormat';

export interface WallClockTimeFieldProps {
  /** 24-hour wall time: HH:mm or HH:mm:ss */
  value: string;
  onChange: (hm: string) => void;
  disabled?: boolean;
  withSeconds?: boolean;
  className?: string;
  id?: string;
  /** Show clock icon (default true) */
  showIcon?: boolean;
}

/**
 * Time input that respects VITE_HOURS_FORMAT.
 * Emits 24-hour HH:mm (or HH:mm:ss) for API / storage.
 */
export function WallClockTimeField({
  value,
  onChange,
  disabled = false,
  withSeconds = false,
  className,
  id,
  showIcon = true,
}: WallClockTimeFieldProps) {
  const normalized = normalizeWallClockHm(value, withSeconds);
  const is12 = getHoursFormat() === '12';

  if (!is12) {
    const [h, m] = normalized.split(':');
    const triggerClass = getTimeSelectTriggerClass(
      disabled ? 'text-foreground disabled:opacity-100' : '',
    );
    if (withSeconds) {
      return (
        <div className={cn('relative', className)}>
          {showIcon ? (
            <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          ) : null}
          <Input
            id={id}
            type="time"
            step={1}
            disabled={disabled}
            className={cn(showIcon && 'pl-9', 'bg-white')}
            value={normalized.slice(0, 8)}
            onChange={(e) =>
              onChange(normalizeWallClockHm(e.target.value, withSeconds))
            }
          />
        </div>
      );
    }
    return (
      <div className={cn('flex flex-wrap items-center gap-1', className)}>
        {showIcon ? (
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : null}
        <Select
          value={h}
          onValueChange={(hour) => onChange(normalizeWallClockHm(`${hour}:${m}`))}
          disabled={disabled}
        >
          <SelectTrigger className={triggerClass} id={id}>
            <SelectValue>{h}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {WALL_CLOCK_HOUR_OPTIONS_24.map((hourVal) => (
              <SelectItem key={hourVal} value={hourVal}>
                {hourVal}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">:</span>
        <Select
          value={m}
          onValueChange={(minute) =>
            onChange(normalizeWallClockHm(`${h}:${minute}`))
          }
          disabled={disabled}
        >
          <SelectTrigger className={triggerClass}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WALL_CLOCK_MINUTE_OPTIONS.map((minVal) => (
              <SelectItem key={minVal} value={minVal}>
                {minVal}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const parts = hm24ToTwelveHourParts(normalized);
  const emit = (next: { hour12?: string; minute?: string; period?: 'AM' | 'PM' }) => {
    onChange(
      twelveHourPartsToHm24({
        hour12: next.hour12 ?? parts.hour12,
        minute: next.minute ?? parts.minute,
        period: next.period ?? parts.period,
      }),
    );
  };

  const triggerClass = getTimeSelectTriggerClass(
    disabled ? 'text-foreground disabled:opacity-100' : '',
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {showIcon ? (
        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : null}
      <Select
        value={parts.hour12}
        onValueChange={(v) => emit({ hour12: v })}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass} id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TWELVE_HOUR_OPTIONS.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select
        value={parts.minute}
        onValueChange={(v) => emit({ minute: v })}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WALL_CLOCK_MINUTE_OPTIONS.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={parts.period}
        onValueChange={(v) => emit({ period: v as 'AM' | 'PM' })}
        disabled={disabled}
      >
        <SelectTrigger className={cn(triggerClass, 'w-[5.25rem]')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/** 24h-only hour:minute selects (labels respect 12h display via formatHourOnlyForDisplay). */
export function WallClockTimeSelects({
  value,
  onChange,
  disabled = false,
  className,
}: Omit<WallClockTimeFieldProps, 'withSeconds' | 'showIcon' | 'id'>) {
  const [h, m] = normalizeWallClockHm(value).split(':');
  const triggerClass = getTimeSelectTriggerClass(
    disabled ? 'text-foreground disabled:opacity-100' : '',
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Select
        value={h}
        onValueChange={(hour) => onChange(normalizeWallClockHm(`${hour}:${m}`))}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass}>
          <SelectValue>{formatHourOnlyForDisplay(h)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {WALL_CLOCK_HOUR_OPTIONS_24.map((hourVal) => (
            <SelectItem key={hourVal} value={hourVal}>
              {formatHourOnlyForDisplay(hourVal)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select
        value={m}
        onValueChange={(minute) => onChange(normalizeWallClockHm(`${h}:${minute}`))}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WALL_CLOCK_MINUTE_OPTIONS.map((minVal) => (
            <SelectItem key={minVal} value={minVal}>
              {minVal}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
